import { formatScheduleForCustomer } from '@/lib/weeklySchedule';
import {
  formatCoreBusinessPaymentBlock,
  matchesCoreBusinessPaymentIntent,
} from '@/lib/assistant/assistantPayments';

function deliveryBasePrice(b) {
  const v = b?.delivery_base_price ?? b?.precio_base_entrega;
  return Number(v) || 0;
}

function deliveryPerKm(b) {
  if (b?.delivery_price_per_km == null || b?.delivery_price_per_km === '') return null;
  const n = Number(b.delivery_price_per_km);
  return Number.isFinite(n) ? n : null;
}

function deliveryMinKm(b) {
  if (b?.delivery_min_distance_km == null || b?.delivery_min_distance_km === '') return null;
  const n = Number(b.delivery_min_distance_km);
  return Number.isFinite(n) ? n : null;
}

function deliveryRadiusKm(b) {
  if (b?.delivery_radius_km == null || b?.delivery_radius_km === '') return null;
  const n = Number(b.delivery_radius_km);
  return Number.isFinite(n) ? n : null;
}

/** Hay al menos un dato de precio/distancia cargado (no inventamos defaults). */
function hasDeliveryPricingConfig(business) {
  if (!business?.service_types?.includes('delivery')) return false;
  const hasBaseField =
    business.delivery_base_price != null ||
    business.precio_base_entrega != null;
  const perKm = deliveryPerKm(business);
  const minKm = deliveryMinKm(business);
  if (hasBaseField || perKm != null || minKm != null) return true;
  const baseNum = deliveryBasePrice(business);
  return baseNum > 0;
}

function formatDeliveryCostBlock(business) {
  if (!business?.service_types?.includes('delivery')) {
    return '🚚 Según lo configurado acá, este local no ofrece delivery.';
  }

  if (!hasDeliveryPricingConfig(business)) {
    const r = deliveryRadiusKm(business);
    const bits = [];
    if (r != null) bits.push(`Radio de cobertura cargado: ${r} km.`);
    bits.push(
      'El precio de envío no está detallado en la configuración que veo acá. Al ingresar tu dirección en el carrito debería calcularse, o consultá al local.'
    );
    return `🚚 Delivery\n\n${bits.join('\n\n')}`.trim();
  }

  const base = deliveryBasePrice(business);
  const perKm = deliveryPerKm(business);
  const minKm = deliveryMinKm(business);
  const radius = deliveryRadiusKm(business);
  const hasBaseDeclared =
    business.delivery_base_price != null || business.precio_base_entrega != null;

  let line;
  if (hasBaseDeclared || base > 0) {
    line = `🚚 El delivery parte en $${base.toLocaleString('es-CL')}`;
    if (minKm != null) {
      line += ` dentro de los primeros ${minKm} km`;
    }
    if (perKm != null) {
      line += ` y luego suma $${perKm.toLocaleString('es-CL')} por km adicional`;
    }
    line += '. Al poner tu dirección en el carrito te mostramos el valor exacto.';
  } else if (perKm != null) {
    line = `🚚 El envío suma $${perKm.toLocaleString('es-CL')} por km según la configuración del local. Al poner tu dirección en el carrito te mostramos el valor exacto.`;
  } else if (minKm != null) {
    line = `🚚 El local tiene configurado un tramo de referencia de ${minKm} km para el delivery. Al poner tu dirección en el carrito te mostramos el valor exacto.`;
  } else {
    line =
      '🚚 Hay datos de delivery cargados. Al poner tu dirección en el carrito te mostramos el valor exacto.';
  }

  const extra = [];
  if (radius != null) {
    extra.push(`Radio máximo de cobertura cargado: ${radius} km.`);
  }
  if (business.delivery_time || business.delivery_time_minutes) {
    const t = business.delivery_time || `~${business.delivery_time_minutes} min`;
    extra.push(`Tiempo indicado en el local: ${t}.`);
  }

  return [line, extra.length ? `\n\n${extra.join('\n')}` : ''].join('').trim();
}

function hasDeliveryZonesList(business) {
  return Array.isArray(business?.delivery_zones) && business.delivery_zones.length > 0;
}

/**
 * Zonas fijas (dashboard): precio y tiempo; el checkout detecta la zona por la dirección.
 */
function formatDeliveryZonesListBlock(business) {
  if (!business?.service_types?.includes('delivery') || !hasDeliveryZonesList(business)) {
    return '🚚 No hay zonas de delivery cargadas. Consultá al local o fijate el menú al pedir.';
  }
  const lines = business.delivery_zones.map((z) => {
    const name = String(z.name || 'Zona').trim();
    const p = Number(z.price) || 0;
    const m = Number(z.estimated_minutes) || 0;
    const timePart = m > 0 ? ` (${m} min aprox)` : '';
    return `${name}: $${p.toLocaleString('es-CL')}${timePart}`;
  });
  return `Sí, hacemos delivery 🚚\n\n${lines.join('\n')}`;
}

/** Intent: el usuario pregunta por despacho (sin caer en horario/ubicación del local sola). */
function matchesCoreDeliveryZonesListIntent(n) {
  if (matchesCoreBusinessScheduleIntent(n) || matchesCoreBusinessLocationIntent(n)) return false;
  return /\b(delivery|despacho|despachan|env[ií]an|hacen env[ií]o|hacen envio|hacen env[ií]os)\b/.test(
    n
  );
}

function formatDeliveryAreaBlock(business) {
  if (!business?.service_types?.includes('delivery')) {
    return '📍 Este local no figura con delivery en la configuración actual.';
  }
  if (hasDeliveryZonesList(business)) {
    return `📍 Cobertura por zonas (referencia; el pedido se confirma con tu dirección en el carrito):\n\n${formatDeliveryZonesListBlock(
      business
    )}`;
  }
  const bits = [];
  if (business.delivery_zone && String(business.delivery_zone).trim()) {
    bits.push(`Zona / referencia cargada: ${String(business.delivery_zone).trim()}`);
  }
  if (business.delivery_radius_km) {
    bits.push(`Radio máximo aproximado: ${business.delivery_radius_km} km desde el local.`);
  }
  if (business.address && String(business.address).trim()) {
    bits.push(`Dirección del local: ${String(business.address).trim()}`);
  }
  bits.push(
    'No tengo calle por calle: la cobertura real depende de la distancia al preparar el pedido.'
  );
  return `📍 Cobertura\n\n${bits.join('\n\n')}`.trim();
}

function formatOwnerContactBlock(business) {
  const wa = String(business?.whatsapp || '').trim();
  if (wa) {
    return `💬 Para hablar directo con el local, lo más rápido es WhatsApp (está cargado en el perfil del negocio).\n\nSi querés, usá el botón de WhatsApp abajo.`;
  }
  return '💬 No figura WhatsApp cargado para este local. Podés intentar por redes o visitar el local si hay dirección.';
}

function formatScheduleBlock(business) {
  const scheduleText = formatScheduleForCustomer(business);
  if (scheduleText) {
    const open = business.is_open
      ? '\n\n✅ Figura como abierto ahora.'
      : `\n\n🔴 ${business.closed_message || 'Cerrado ahora según la configuración.'}`;
    return `🕐 Horario\n\n${scheduleText}${open}`;
  }
  return '🕐 No hay horario detallado cargado acá. Te conviene confirmar por WhatsApp o en el local.';
}

/** Preguntas operativas del negocio (core, no training): horarios, pagos, ubicación, retiro. `n` = texto normalizado. */
function matchesCoreBusinessScheduleIntent(n) {
  if (/\b(nombre|tienen|tienes|tengo|gusto|foto)\b/.test(n) && !/\b(horario|hora|abren|cierran)\b/.test(n)) {
    return false;
  }
  // `n` ya viene sin tildes (normalizeAssistantText)
  return (
    /\b(horario|horarios|abierto|abiertos|cerrado|cierra|cierran|abren|atienden|atienen)\b/.test(n) ||
    /\b(a que hora|que hora|hasta que hora|desde que hora)\b/.test(n) ||
    (/\b(cuando)\b/.test(n) && /\b(abren|cierran|cierra|abre|atien|atienden|abierto|abiertos|horario)\b/.test(n))
  );
}

function matchesCoreBusinessLocationIntent(n) {
  if (matchesCoreBusinessScheduleIntent(n) || matchesCoreBusinessPaymentIntent(n)) return false;
  if (/\b(cobertura|manda[sn] a|zona|comuna|radio|envio|envío a)\b/.test(n) && !/\b(direc|direccion|donde|ubic|local)\b/.test(n)) {
    return false;
  }
  return (
    /\b(direcci[oó]n|direccion|ubicaci[oó]n|ubicacion|ubicad[oa]s?|sucursal)\b/.test(n) ||
    (/\b(donde)\b/.test(n) && /\b(estan|queda|quedan|juegan|ubican|local|negocio|sucursal|parque|frente|numero)\b/.test(n))
  );
}

function looksLikeServiceTypesQuestion(n) {
  if (/\b(a que hora|a qué hora|horario|abierto|cierra)\b/.test(n)) return false;
  if (/\b(cuanto|cuánto|cuesta|precio|costo)\b.*\b(delivery|envio|envío)\b/.test(n)) return false;
  return (
    /\b(delivery|domicilio|envio|envío|reparto)\b/.test(n) ||
    /\b(retiro|retirar|pasar\s+a\s+buscar|para\s+llevar|take\s*away)\b/.test(n) ||
    /\b(ir\s+al\s+local|pasar\s+por\s+el\s+local)\b/.test(n) ||
    /\b(mesa|en\s+el\s+local|local)\b/.test(n) ||
    /\b(comer\s+(ahi|ahí|allá|all[ií]|aca|acá))\b/.test(n) ||
    /\b(atienden\s+en|atienden\s+el|puedo\s+comer|se\s+puede\s+comer)\b/.test(n) ||
    /\b(es\s+)?(solo|s[oó]lo)\s+delivery\b/.test(n) ||
    /\b(tienen\s+delivery|tienes\s+delivery|hay\s+delivery|hacen\s+delivery)\b/.test(n) ||
    /\b(puedo\s+retirar|se\s+puede\s+retirar)\b/.test(n) ||
    /\b(pedir\s+para\s+llevar)\b/.test(n) ||
    /\b(puedo\s+ir|voy a buscar|ir a buscar|ir a retir|recoger|recojo|buscar (el |mi )?pedido|paso a buscar|levantar (el |mi )?pedido)\b/.test(
      n
    )
  );
}

/**
 * Retiro / ir a buscar: solo si NO cae en la pregunta general de servicios
 * (evita doble respuesta; looksLikeServiceTypesQuestion se resuelve después).
 */
function matchesCoreBusinessPickupFaqOnly(n) {
  if (looksLikeServiceTypesQuestion(n)) return false;
  if (matchesCoreBusinessScheduleIntent(n) || matchesCoreBusinessLocationIntent(n) || matchesCoreBusinessPaymentIntent(n)) {
    return false;
  }
  return /\b(puedo ir|puedo pasar|voy a buscar|voy a retirar|ir a buscar|ir a retir|recoger|recojo|recog|buscar (el |mi )?pedido|busco el pedido|paso a buscar|levantar (el |mi )?pedido|pasar a buscar)\b/.test(
    n
  );
}

function joinNaturalSpanishList(parts) {
  const p = (parts || []).filter(Boolean);
  if (p.length === 0) return '';
  if (p.length === 1) return p[0];
  if (p.length === 2) return `${p[0]} y ${p[1]}`;
  return `${p.slice(0, -1).join(', ')} y ${p[p.length - 1]}`;
}

function formatCoreBusinessLocationBlock(business) {
  const a = business?.address && String(business.address).trim();
  if (a) {
    return `📍 Estamos en ${a}.\n\nSi necesitás referencia extra, fijate el mapa en el menú o escribime por WhatsApp.`;
  }
  return '📍 No tengo la dirección guardada en la ficha. Podés verla en el menú o escribir al local por WhatsApp y te la pasan seguro.';
}

function formatCoreBusinessPickupFaqBlock(business) {
  const st = business?.service_types || [];
  const hasRet = st.includes('retiro');
  const addr = business?.address && String(business.address).trim();
  if (hasRet) {
    if (addr) {
      return `Sí, podés retirar tu pedido en el local 😊 Nos encontrás en ${addr}.\n\nSi te perdés, pídele el mapa al local por WhatsApp.`;
    }
    return 'Sí, acá se puede retirar en el local según la configuración. La dire la ves en el menú o me escribís por WhatsApp. 😊';
  }
  return 'Ahora, según lo que figura, no tengo retiro en local habilitado. Consultá en el menú o por WhatsApp por si se puede coordinar. 😊';
}

/**
 * Intents: horario, medios de pago, dónde queda el local, retiro/ir a buscar (suelto).
 * Devuelve null si no aplica. Prioridad: horario → pagos → ubicación → retiro.
 */
export function tryCoreBusinessInfoFaq(n, business, defaultFallback) {
  if (matchesCoreBusinessScheduleIntent(n)) {
    return { text: formatScheduleBlock(business), products: null, fallbackKeys: defaultFallback };
  }
  if (matchesCoreBusinessPaymentIntent(n)) {
    return { text: formatCoreBusinessPaymentBlock(business), products: null, fallbackKeys: defaultFallback };
  }
  if (matchesCoreBusinessLocationIntent(n)) {
    return { text: formatCoreBusinessLocationBlock(business), products: null, fallbackKeys: defaultFallback };
  }
  if (matchesCoreBusinessPickupFaqOnly(n)) {
    return { text: formatCoreBusinessPickupFaqBlock(business), products: null, fallbackKeys: defaultFallback };
  }
  return null;
}

export {
  formatDeliveryCostBlock,
  formatDeliveryAreaBlock,
  formatOwnerContactBlock,
  formatDeliveryZonesListBlock,
  hasDeliveryZonesList,
  matchesCoreDeliveryZonesListIntent,
  looksLikeServiceTypesQuestion,
};
